import React from 'react';
import { 
  Drawer, Box, Typography, IconButton, Divider,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  TextField, Button, Chip
} from '@mui/material';
import { Close, Delete, LocalOffer } from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import noPhoto from "../assets/no_photo.webp";


const CartDrawer = () => {
  const navigate = useNavigate();
  const {
    cart,
    cartOpen,
    setCartOpen,
    updateQuantity,
    removeFromCart,
    cartTotal,
    totalSavings,
    loading,
    getItemPrice,
    getOriginalPrice,
    hasDiscount,
    deliverFees
  } = useCart();

  return (
    <Drawer
      anchor="right"
      dir='rtl'
      open={cartOpen}
      onClose={() => setCartOpen(false)}
      PaperProps={{ sx: { width: 400, maxWidth: '90vw', p: 2 } }}
      sx={{zIndex: 1201}}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">السلة</Typography>
        <IconButton onClick={() => setCartOpen(false)}>
          <Close />
        </IconButton>
      </Box>

      <Divider />

      {loading ? (
        <Box p={3} textAlign="center">
          <Typography>جار تحميل السلة ...</Typography>
        </Box>
      ) : cart.length === 0 ? (
        <Box p={3} textAlign="center">
          <Typography>السلة فارغة</Typography>
        </Box>
      ) : (
        <>
          <List>
            {cart.map((item) => {
              const currentPrice = getItemPrice(item);
              const originalPrice = getOriginalPrice(item);
              const isDiscounted = hasDiscount(item);
              const discountPercent = isDiscounted 
                ? Math.round((1 - currentPrice / originalPrice) * 100)
                : 0;

              return (
                <ListItem key={item.id} sx={{ alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ mr: 2, width: 60, height: 60, position: 'relative' }}>
                    <img
                      src={item.product?.imageUrl || noPhoto}
                      alt={item.product?.name}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {isDiscounted && (
                      <Chip
                        label={`-${discountPercent}%`}
                        size="small"
                        color="error"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          fontSize: '0.6rem',
                          height: 20,
                          minWidth: 30
                        }}
                      />
                    )}
                  </Box>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {item.product?.name}
                    </Typography>
                    
                    {/* Price Display */}
                    {isDiscounted ? (
                      <Box>
                        <Typography variant="body2" color="error" fontWeight="bold">
                          E.G.P {currentPrice.toFixed(2)} each
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            textDecoration: 'line-through', 
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                        >
                          E.G.P {originalPrice.toFixed(2)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        E.G.P {currentPrice.toFixed(2)} each
                      </Typography>
                    )}
                    
                    {/* Quantity Controls */}
                    <Box display="flex" alignItems="center" mt={1}>
                      <Button 
                        size="small" 
                        onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                        sx={{ minWidth: 30, height: 30 }}
                      >-</Button>
                      <TextField
                        type="number"
                        size="small"
                        value={item.quantity}
                        onChange={(e) => {
                          const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                          updateQuantity(item.id, newQuantity);
                        }}
                        inputProps={{ min: 1 }}
                        sx={{ width: 60, mx: 1, '& .MuiInputBase-input': { textAlign: 'center' } }}
                      />
                      <Button 
                        size="small" 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        sx={{ minWidth: 30, height: 30 }}
                      >+</Button>
                    </Box>
                  </Box>
                  
                  <Box sx={{ textAlign: 'right', ml: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      E.G.P {(currentPrice * item.quantity).toFixed(2)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeFromCart(item.id)}
                      color="error"
                      sx={{ mt: 1 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ p: 2 }}>
            {/* Total Savings */}
            {totalSavings > 0 && (
              <Box sx={{ 
                backgroundColor: 'success.light', 
                p: 1.5, 
                borderRadius: 1, 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <LocalOffer fontSize="small" color="success" />
                <Typography variant="body2" color="success.dark" fontWeight="medium">
                  لقد وفرت {totalSavings.toFixed(2)} جنيه!
                </Typography>
              </Box>
            )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">رسوم التوصيل :</Typography>
                  <Typography variant="body2" color="primary.main">
                    E.G.P {deliverFees.toFixed(2)}
                  </Typography>
              </Box>
            {/* Order Total */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">الإجمالي:</Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                E.G.P {cartTotal.toFixed(2)}
              </Typography>
            </Box>

            {/* Item Count */}
            <Typography variant="body2" color="text.secondary" textAlign="right" sx={{ mb: 2 }}>
              {cart.reduce((sum, item) => sum + item.quantity, 0)} منتج
            </Typography>

            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{ 
                mt: 1,
                backgroundColor: totalSavings > 0 ? 'success.main' : 'primary.main',
                '&:hover': {
                  backgroundColor: totalSavings > 0 ? 'success.dark' : 'primary.dark'
                }
              }}
              onClick={() => {
                setCartOpen(false);
                navigate('/checkout');
              }}
            >
              {totalSavings > 0 ? 'الدفع مع التوفير' : 'الذهاب للدفع'}
            </Button>

            {/* Continue Shopping Button */}
            <Button
              fullWidth
              variant="outlined"
              size="medium"
              sx={{ mt: 1 }}
              onClick={() => setCartOpen(false)}
            >
              إستكمال التسوق
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default CartDrawer;